/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  CreatableSingleSelect,
  CreatableMultiSelect,
} from '@ouster/webviz-react-components';
import type { CreatedOption } from '@ouster/webviz-react-components';

import { isNull, uniq } from 'lodash';
import { useEffect, useCallback, useMemo, useState } from 'react';

import styles from './UploadAttributes.module.css';
import useChakraHook from '../hooks/useChakraHook';
// eslint-disable-next-line import/no-cycle
import { useStoreState, useStoreActions } from '../reducers';
import {
  DataCategory,
  Tag,
  MappedDataCategory,
  NullableMappedDataCategory,
  MappedTag,
} from '../types';
import { formatTag, mapData } from '../utils/misc';

type UploadAttributesProps = {
  selectedDataCategory?: DataCategory;
  selectedTags: Tag[];
  onDataCategoryChange: (dataCategoryId?: DataCategory) => void;
  onTagChange: (tags: Tag[]) => void;
};

export default function UploadAttributes({
  selectedDataCategory,
  selectedTags,
  onDataCategoryChange,
  onTagChange,
}: UploadAttributesProps) {
  const { dataCategories } = useStoreState((storeState) => ({
    ...storeState.dataCategory,
  }));
  const { tags } = useStoreState((storeState) => ({
    ...storeState.recordingTag,
  }));
  const { curUserOrg, isOusterUser } = useStoreState((storeState) => ({
    ...storeState.user,
  }));
  const { addDataCategory, loadDataCategories } = useStoreActions(
    (actions) => actions.dataCategory
  );
  const { fetchTags, createTag } = useStoreActions(
    (actions) => actions.recordingTag
  );

  const [isCategoryInCreation, setIsCategoryInCreation] = useState(false);
  const [isTagInCreation, setIsTagInCreation] = useState(false);

  const { showToast } = useChakraHook();

  useEffect(() => {
    /* 
      For non-ouster organization, all the upload will go to default category
      and we will set it up automatically here.
    */
    if (!isOusterUser && dataCategories?.length === 1) {
      onDataCategoryChange(dataCategories[0]);
    }
  }, [dataCategories, isOusterUser, onDataCategoryChange]);

  useEffect(() => {
    if (curUserOrg?.id) {
      loadDataCategories(curUserOrg.id);
      fetchTags(curUserOrg.id);
    }
  }, [curUserOrg?.id, fetchTags, loadDataCategories]);

  const noExistingCategories = dataCategories?.length === 0;
  const noExistingTags = tags?.length === 0;

  // show categories in alphabet order
  const mappedDataCategory: MappedDataCategory[] = useMemo(
    () => mapData<DataCategory>(dataCategories || []),
    [dataCategories]
  );

  // selected category is an object, hence we can't use our map function above
  const mappedSelectedDataCategory: NullableMappedDataCategory = useMemo(
    () =>
      selectedDataCategory
        ? {
            label: selectedDataCategory.name,
            value: selectedDataCategory.name,
            data: selectedDataCategory,
          }
        : null,
    [selectedDataCategory]
  );

  // show tags in alphabetic order
  const mappedTags: MappedTag[] = useMemo(() => mapData<Tag>(tags), [tags]);

  // keep the selected tags in the same order as the user selected
  const mappedSelectedTags: MappedTag[] = useMemo(
    () => mapData<Tag>(selectedTags),
    [selectedTags]
  );

  const onAddCategory = useCallback(
    async (name: string) => {
      if (curUserOrg?.id) {
        try {
          setIsCategoryInCreation(true);
          const category = await addDataCategory({
            organization_id: curUserOrg.id,
            name,
          });
          onDataCategoryChange(category);
        } catch (error) {
          showToast({
            title: 'Failed to add category',
            description: `${error}`,
            status: 'error',
          });
        } finally {
          setIsCategoryInCreation(false);
        }
      }
    },
    [addDataCategory, curUserOrg?.id, onDataCategoryChange, showToast]
  );

  const onCreateTag = useCallback(
    async (name: string) => {
      if (curUserOrg?.id) {
        try {
          setIsTagInCreation(true);
          const tag = await createTag({
            organization_id: curUserOrg.id,
            name,
          });
          onTagChange([...selectedTags, tag]);
        } catch (error) {
          showToast({
            title: 'Failed to add tag',
            description: `${error}`,
            status: 'error',
          });
        } finally {
          setIsTagInCreation(false);
        }
      }
    },
    [createTag, curUserOrg?.id, onTagChange, selectedTags, showToast]
  );

  const handleCategoryChange = useCallback(
    (item?: DataCategory | string | null) => {
      if (isNull(item)) {
        onDataCategoryChange(undefined);
        return;
      }
      if (typeof item === 'string') {
        const existingOption = dataCategories?.find(
          (option) => option.name.toLowerCase() === item.toLowerCase()
        );

        if (existingOption) {
          // Use the existing option
          onDataCategoryChange(existingOption);
        } else {
          // Created new option
          onAddCategory(item);
        }
        return;
      }

      onDataCategoryChange(item);
    },
    [onAddCategory, dataCategories, onDataCategoryChange]
  );

  const handleTagChange = useCallback(
    (items: readonly (CreatedOption | MappedTag)[]) => {
      if (items.length === 0) {
        onTagChange([]);
        return;
      }
      const tempTags: Tag[] = [];
      items.forEach((tag) => {
        if ('__isNew__' in tag) {
          // newly created tag, check whether it existed
          const existingTag = tags.find((t) => t.name === formatTag(tag.value));
          if (existingTag) {
            tempTags.push(existingTag);
          } else {
            // If tag doesn't exist, create a new tag and add it
            onCreateTag(formatTag(tag.value));
          }
        } else {
          tempTags.push(tag.data);
        }
      });

      onTagChange(uniq(tempTags));
    },
    [onCreateTag, tags, onTagChange]
  );

  return (
    <div className={styles['attributes-container']} data-testid="upload-attributes-root">
      {/* category selector */}
      {isOusterUser && (
        <div data-testid="upload-attributes-category-section">
          <h3 className={styles.title} data-testid="upload-attributes-category-title">
            Category<sup>*</sup>
          </h3>
          <CreatableSingleSelect
            placeholderText={
              noExistingCategories
                ? 'Add a data category...'
                : 'Search or add a data category...'
            }
            menuPlacement="bottom"
            options={mappedDataCategory}
            onOptionChange={handleCategoryChange}
            selectedOption={mappedSelectedDataCategory}
            isLoading={isCategoryInCreation}
            data-testid="upload-attributes-category-select"
          />
          <div className={styles.hint} data-testid="upload-attributes-category-hint">
            {noExistingCategories
              ? 'Start typing to add a new data category. Categories help classify your data for easier access.'
              : 'Search or add a data category. Use data categories to accurately classify your data.'}
          </div>
        </div>
      )}
      {/* tag selector */}

      <div data-testid="upload-attributes-tags-section">
        <h3 className={styles.title} data-testid="upload-attributes-tags-title">Tags</h3>
        <CreatableMultiSelect
          placeholderText={
            noExistingTags ? 'Add a tag...' : 'Search or add tags...'
          }
          menuPlacement={isOusterUser ? 'auto' : 'bottom'}
          options={mappedTags}
          onOptionChange={handleTagChange}
          selectedOptions={mappedSelectedTags}
          formatLabel={(inputValue: string) => formatTag(inputValue)}
          isLoading={isTagInCreation}
          isCreatable
          data-testid="upload-attributes-tags-select"
        />
        <div className={styles.hint} data-testid="upload-attributes-tags-hint">
          {noExistingTags
            ? 'Start typing to add a tag. Tags add descriptive labels for quick search.'
            : 'Search or add tags. Apply tags for precise data description.'}
        </div>
      </div>
    </div>
  );
}
